import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  FiSearch,
  FiCheck,
  FiX,
  FiEye,
  FiChevronLeft,
  FiChevronRight,
} from "react-icons/fi";
import { CiImageOn } from "react-icons/ci";
import { submissionApi, qc_status } from "../../api/HttpApis";
import { showToast } from "../../utils/Toastify/Toastify";
import RejectModal from "./RejectModal";
import ConfirmDialog from "./ConfirmDialog";
import style from "./Submissions.module.css";
import useUrlTab from "../../hooks/useUrlTab";

const TABS = ["Pending", "Approved", "Rejected", "All"];
const PAGE_SIZES = [8, 16, 24];

// "36d waiting" style relative label from the submission date.
const waitingLabel = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  const days = Math.max(0, Math.floor((Date.now() - d.getTime()) / 86400000));
  if (days === 0) return "today";
  return `${days}d waiting`;
};

const fmtSubmitted = (dateStr) => {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric" });
};

const badgeClass = (qc) =>
  qc === "Approved" ? style.approved : qc === "Rejected" ? style.rejected : style.pending;

function Thumb({ src, alt }) {
  const [err, setErr] = useState(false);
  if (!src || err)
    return (
      <div className={style.thumb}>
        <CiImageOn size={22} />
      </div>
    );
  return (
    <div className={style.thumb}>
      <img src={src} alt={alt || ""} onError={() => setErr(true)} />
    </div>
  );
}

const Submissions = () => {
  const navigate = useNavigate();

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({ All: 0, Pending: 0, Approved: 0, Rejected: 0 });
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null); // per-row action lock

  const [tab, setTab] = useUrlTab(TABS, "Pending");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("All");
  const [creatorFilter, setCreatorFilter] = useState("All");
  const [sort, setSort] = useState("oldest");

  const [selected, setSelected] = useState(() => new Set());
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(8);

  // Dropdown options (Type / Creator) come from a one-time facet fetch so they
  // list ALL values, not just those on the current server page.
  const [contentTypes, setContentTypes] = useState(["All"]);
  const [creators, setCreators] = useState(["All"]);

  // reject modal state — { ids: number[], title } or null
  const [rejectTarget, setRejectTarget] = useState(null);
  const [rejecting, setRejecting] = useState(false);

  // approve confirm state — { ids: number[], title } or null
  const [approveTarget, setApproveTarget] = useState(null);

  const mapRow = (item) => ({
    id: item.id,
    name: item.name || "Untitled",
    media: item.portrait,
    creator: item.creator?.name || "Unknown",
    // The full creator object too: the takedown dialog needs to know whether
    // this creator publishes without review, which the flattened display
    // name above cannot carry.
    creatorInfo: item.creator || null,
    contentType: item["content-type"] || "—",
    qcStatus: item.qc_status || "Pending",
    submittedAt: item.submission_date || item.created_at || null,
    // A Series row represents all of its episodes: episodeIds is what
    // Approve/Reject actually acts on (every episode at once). `id` is the
    // series' own id for a grouped row, which lives in a different id space
    // than a plain video's meta id — rowKey disambiguates the two so a
    // series id can never collide with a movie's meta id as a React/selection key.
    groupType: item.group_type || "video",
    rowKey: `${item.group_type || "video"}:${item.id}`,
    episodeIds: Array.isArray(item.episode_ids) && item.episode_ids.length ? item.episode_ids : [item.id],
    episodeCount: item.episode_count || 1,
  });

  // Debounce the search box so we don't hit the server on every keystroke.
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(t);
  }, [search]);

  // Server-side fetch: filters, sort, and pagination all happen on the backend.
  // The numeric-ID shortcut is applied client-side after fetch (rare, cheap).
  const load = async () => {
    setLoading(true);
    try {
      const filters = {};
      if (debouncedSearch.trim()) filters.search = debouncedSearch.trim();
      if (tab !== "All") filters.qc_status = tab;
      if (typeFilter !== "All") filters.content_type = typeFilter;
      if (creatorFilter !== "All") filters.creator = creatorFilter;
      if (sort) filters.sort = sort;

      const res = await submissionApi(filters, pageSize, page);
      const data = Array.isArray(res?.data?.data) ? res.data.data : [];
      setRows(data.map(mapRow));
      setTotal(res?.data?.total || 0);
      if (res?.data?.counts) setCounts(res.data.counts);
    } catch (e) {
      console.error("Failed to fetch submissions:", e);
      showToast("Failed to load submissions", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, debouncedSearch, typeFilter, creatorFilter, sort, page, pageSize]);

  // Populate the Type / Creator dropdowns once from a wide facet fetch.
  useEffect(() => {
    (async () => {
      try {
        const res = await submissionApi({}, 1000, 1);
        const data = Array.isArray(res?.data?.data) ? res.data.data : [];
        setContentTypes([
          "All",
          ...Array.from(
            new Set(data.map((d) => d["content-type"]).filter((v) => v && v !== "—"))
          ),
        ]);
        setCreators([
          "All",
          ...Array.from(
            new Set(data.map((d) => d.creator?.name).filter(Boolean))
          ),
        ]);
      } catch (e) {
        // Non-fatal: dropdowns just fall back to "All".
      }
    })();
  }, []);

  // Reset to page 1 whenever the view (tab/filter/sort/search) changes.
  useEffect(() => {
    setPage(1);
    setSelected(new Set());
  }, [tab, debouncedSearch, typeFilter, creatorFilter, sort, pageSize]);

  // Numeric-ID search is a client-side convenience on top of the server rows.
  const pageRows = useMemo(() => {
    const q = debouncedSearch.trim();
    if (q && /^\d+$/.test(q)) {
      return rows.filter((r) => String(r.id).includes(q));
    }
    return rows;
  }, [rows, debouncedSearch]);

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  const toggleRow = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const clearSelection = () => setSelected(new Set());

  // Only Pending rows show a checkbox at all (see the row render below), so
  // "select all" only ever means "all selectable rows on this page".
  const selectableKeys = useMemo(
    () => pageRows.filter((r) => r.qcStatus === "Pending").map((r) => r.rowKey),
    [pageRows]
  );
  const allPageSelected =
    selectableKeys.length > 0 && selectableKeys.every((k) => selected.has(k));
  const toggleSelectAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allPageSelected) {
        selectableKeys.forEach((k) => next.delete(k));
      } else {
        selectableKeys.forEach((k) => next.add(k));
      }
      return next;
    });
  };

  // A Series row groups all of its episodes — clicking it opens the episode
  // list for that series instead of a single video's review page. A plain
  // video row (movie, etc.) goes straight to its own review page.
  const openReview = (item) =>
    item.groupType === "series"
      ? navigate(`/mainlayout/submissions/series/${item.id}`)
      : navigate(`/mainlayout/submissions/${item.episodeIds[0]}`);

  // Row keys selected via checkbox — expand each back to the underlying
  // video_metadata id(s) so Approve/Reject act on every episode of a grouped
  // series row.
  const expandToEpisodeIds = (rowKeys) => {
    const byKey = new Map(rows.map((r) => [r.rowKey, r.episodeIds]));
    const out = [];
    for (const key of rowKeys) {
      out.push(...(byKey.get(key) || []));
    }
    return out;
  };

  const doApprove = async ({ notify, trustCreator }) => {
    if (!approveTarget) return;
    const list = approveTarget.ids;
    const isSingleRow = !!approveTarget.rowKey;
    setBusyId(approveTarget.rowKey || "bulk");
    try {
      const results = await Promise.all(
        list.map((id) =>
          qc_status(id, {
            status: "Approved",
            notify,
            // Never send this on a bulk approve: a selection can span several
            // creators, so one checkbox would silently trust all of them. The
            // dialog already hides the option for a multi-row target; this
            // keeps the invariant in the code that sends the request rather
            // than relying on that UI flag alone.
            //
            // Keyed on the ROW, not on list.length: a single Series row expands
            // to one id per episode, so counting ids would refuse to trust the
            // creator of anything but a one-episode title. Only a row target
            // carries rowKey; bulk targets never do.
            trust_creator: isSingleRow ? !!trustCreator : false,
          })
        )
      );
      // Surface the API's message on a single approve so "…will now publish
      // without review" reaches the admin here too, not only on the detail page.
      showToast(
        isSingleRow
          ? results[0]?.data?.message || "Submission approved"
          : `${list.length} submissions approved`,
        "success"
      );
      setApproveTarget(null);
      clearSelection();
      await load();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to approve", "error");
    } finally {
      setBusyId(null);
    }
  };

  const doReject = async ({ reason, note, notify, removeTrust }) => {
    if (!rejectTarget) return;
    const ids = rejectTarget.ids;
    setRejecting(true);
    try {
      await Promise.all(
        ids.map((id) =>
          qc_status(id, {
            status: "Rejected",
            reason,
            note,
            notify,
            // Takedown only, single row only — never on a bulk reject, where
            // the selection can span several creators. A takedown target is
            // always built from one row, so `creator` being present is what
            // marks it single-creator (see the approve path's note on why id
            // count is the wrong thing to measure).
            remove_trust:
              rejectTarget.takedown && !!rejectTarget.creator
                ? !!removeTrust
                : false,
          })
        )
      );
      showToast(
        rejectTarget.takedown
          ? "Taken down — this content is no longer live"
          : rejectTarget.rowKey
          ? "Submission rejected"
          : `${ids.length} submissions rejected`,
        "success"
      );
      setRejectTarget(null);
      clearSelection();
      await load();
    } catch (err) {
      showToast(err?.response?.data?.message || "Failed to reject", "error");
    } finally {
      setRejecting(false);
    }
  };

  const stats = [
    { label: "Total", value: counts.All, cls: "" },
    { label: "Pending Review", value: counts.Pending, cls: style.warning },
    { label: "Approved", value: counts.Approved, cls: style.success },
  ];

  return (
    <div className={style.page}>
      <div className={style.header}>
        <h1 className={style.heading}>Submissions</h1>
        <span className={style.headingCount}>{counts.All} total</span>
      </div>

      <div className={style.stats}>
        {stats.map((s) => (
          <div className={style.statCard} key={s.label}>
            <div className={style.statLabel}>{s.label}</div>
            <div className={`${style.statValue} ${s.cls}`}>{s.value}</div>
          </div>
        ))}
      </div>

      <div className={style.tabs}>
        {TABS.map((t) => (
          <button
            key={t}
            className={`${style.tab} ${tab === t ? style.tabActive : ""}`}
            onClick={() => setTab(t)}
          >
            {t}
            <span className={style.tabBadge}>{counts[t]}</span>
          </button>
        ))}
      </div>

      <div className={style.toolbar}>
        <div className={style.searchWrap}>
          <FiSearch className={style.searchIcon} size={16} />
          <input
            className={style.searchInput}
            placeholder="Search by title, creator, or ID"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <select
          className={style.select}
          value={typeFilter}
          onChange={(e) => setTypeFilter(e.target.value)}
        >
          {contentTypes.map((t) => (
            <option key={t} value={t}>
              {t === "All" ? "Type: All" : t}
            </option>
          ))}
        </select>
        <select
          className={style.select}
          value={creatorFilter}
          onChange={(e) => setCreatorFilter(e.target.value)}
        >
          {creators.map((c) => (
            <option key={c} value={c}>
              {c === "All" ? "Creator: All" : c}
            </option>
          ))}
        </select>
        <select
          className={style.select}
          value={sort}
          onChange={(e) => setSort(e.target.value)}
        >
          <option value="oldest">Sort: Oldest first</option>
          <option value="newest">Sort: Newest first</option>
          <option value="az">Sort: A–Z</option>
        </select>
      </div>

      {selectableKeys.length > 0 && (
        <div className={style.bulkBar}>
          <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
            <input
              type="checkbox"
              className={style.checkbox}
              checked={allPageSelected}
              onChange={toggleSelectAll}
            />
            <span className={style.bulkCount}>
              {selected.size > 0 ? `${selected.size} selected` : "Select all"}
            </span>
          </label>
          {selected.size > 0 && (
            <>
              <button
                className={style.bulkBtn}
                disabled={busyId === "bulk"}
                onClick={() =>
                  setApproveTarget({
                    ids: expandToEpisodeIds([...selected]),
                    title: `${selected.size} submissions`,
                  })
                }
              >
                Approve selected
              </button>
              <button
                className={style.bulkBtn}
                onClick={() =>
                  setRejectTarget({
                    ids: expandToEpisodeIds([...selected]),
                    title: `${selected.size} submissions`,
                  })
                }
              >
                Reject selected
              </button>
              <button className={style.bulkClear} onClick={clearSelection}>
                Clear
              </button>
            </>
          )}
        </div>
      )}

      {loading ? (
        <div className={style.stateBox}>Loading submissions…</div>
      ) : pageRows.length === 0 ? (
        <div className={style.stateBox}>No submissions in “{tab}”.</div>
      ) : (
        <div className={style.rows}>
          {pageRows.map((item) => {
            const isPending = item.qcStatus === "Pending";
            const sel = selected.has(item.rowKey);
            const submitted = fmtSubmitted(item.submittedAt);
            const meta = [
              item.contentType,
              item.episodeCount > 1 ? `${item.episodeCount} episodes` : null,
              item.creator,
              submitted ? `submitted ${submitted}` : null,
              `SUB-${item.id}`,
            ]
              .filter(Boolean)
              .join(" · ");
            return (
              <div
                key={item.rowKey}
                className={`${style.row} ${sel ? style.rowSelected : ""}`}
              >
                {isPending ? (
                  <input
                    type="checkbox"
                    className={style.checkbox}
                    checked={sel}
                    onChange={() => toggleRow(item.rowKey)}
                  />
                ) : (
                  <span style={{ width: 18, flexShrink: 0 }} />
                )}
                <Thumb src={item.media} alt={item.name} />
                <div className={style.rowInfo} onClick={() => openReview(item)}>
                  <div className={style.rowTitle}>{item.name}</div>
                  <div className={style.rowMeta}>{meta}</div>
                </div>
                {isPending && waitingLabel(item.submittedAt) && (
                  <span className={style.waiting}>{waitingLabel(item.submittedAt)}</span>
                )}
                <span className={`${style.badge} ${badgeClass(item.qcStatus)}`}>
                  {item.qcStatus}
                </span>
                <div className={style.rowActions}>
                  {isPending && (
                    <>
                      <button
                        className={style.approveBtn}
                        disabled={busyId === item.rowKey || busyId === "bulk"}
                        onClick={() =>
                          setApproveTarget({
                            ids: item.episodeIds,
                            title: item.name,
                            rowKey: item.rowKey,
                            creator: item.creatorInfo,
                          })
                        }
                      >
                        <FiCheck size={13} /> Approve
                      </button>
                      <button
                        className={style.rejectBtn}
                        disabled={busyId === item.rowKey || busyId === "bulk"}
                        onClick={() =>
                          setRejectTarget({ ids: item.episodeIds, title: item.name, rowKey: item.rowKey })
                        }
                      >
                        <FiX size={13} /> Reject
                      </button>
                    </>
                  )}
                  {/* Already live. A trusted creator's uploads publish with no
                      review, so there has to be a one-click way to pull one. */}
                  {item.qcStatus === "Approved" && (
                    <button
                      className={style.rejectBtn}
                      disabled={busyId === item.id || busyId === "bulk"}
                      onClick={() =>
                        setRejectTarget({
                          ids: [item.id],
                          title: item.name,
                          takedown: true,
                          creator: item.creatorInfo,
                        })
                      }
                    >
                      <FiX size={13} /> Take down
                    </button>
                  )}
                  <button
                    className={style.eyeBtn}
                    title="Review"
                    onClick={() => openReview(item)}
                  >
                    <FiEye size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!loading && total > 0 && (
        <div className={style.footer}>
          <span className={style.showing}>
            Showing {(page - 1) * pageSize + 1}–
            {Math.min(page * pageSize, total)} of {total}
          </span>
          <div className={style.pager}>
            <select
              className={style.select}
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {PAGE_SIZES.map((n) => (
                <option key={n} value={n}>
                  {n} / page
                </option>
              ))}
            </select>
            <button
              className={style.pageBtn}
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
            >
              <FiChevronLeft size={16} />
            </button>
            <span className={style.pageInfo}>
              {page} / {totalPages}
            </span>
            <button
              className={style.pageBtn}
              disabled={page >= totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            >
              <FiChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      <RejectModal
        open={!!rejectTarget}
        takedown={!!rejectTarget?.takedown}
        canRemoveTrust={!!rejectTarget?.creator?.is_trusted_creator}
        creatorName={rejectTarget?.creator?.name || ""}
        title={rejectTarget?.title}
        submitting={rejecting}
        onCancel={() => setRejectTarget(null)}
        onConfirm={doReject}
      />

      <ConfirmDialog
        open={!!approveTarget}
        tone="success"
        confirmLabel="Approve"
        title="Approve submission"
        message={
          approveTarget
            ? `Approve ${
                approveTarget.rowKey
                  ? `“${approveTarget.title}”`
                  : `${approveTarget.ids.length} submissions`
              }? Once approved it becomes live-ready.`
            : ""
        }
        showNotify
        showTrustCreator={
          // Single ROW, not single id: a Series row carries one id per episode.
          !!approveTarget?.rowKey &&
          !!approveTarget?.creator &&
          !approveTarget.creator.is_trusted_creator
        }
        trustCreatorLabel={`Trust ${
          approveTarget?.creator?.name || "this creator"
        } — publish their future content without review`}
        trustCreatorHelper="Their later uploads and edits go live immediately instead of waiting here. This submission is approved either way."
        submitting={busyId !== null}
        onCancel={() => setApproveTarget(null)}
        onConfirm={doApprove}
      />
    </div>
  );
};

export default Submissions;
